import { PageStatus, PageTypes } from "@/App";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@talentech/components";
import { ProjectData } from "grapesjs";
import type { FrameHeadResult, HeadReplacement } from "@/pages/Editor/utils/frameSetting";

export interface Revision {
  date: string;
  url: string;
}

export interface Template {
  config_draft_url?: string;
  config_published_url?: string;
  draft: boolean;
  html_draft_url?: string;
  html_published_url?: string;
  id: string;
  name: string;
  published: boolean;
  draft_slug?: string;
  prod_slug?: string;
  html_draft_revision_urls: Revision[];
  config_draft_revision_urls: Revision[];
}

export interface Templates {
  job_templates: Template[];
  page_templates: Template[];
}

export interface DeleteTemplatePayload {
  id: string;
  type: PageTypes;
  status: PageStatus;
}

export interface CloneTemplatePayload {
  id: string;
  type: PageTypes;
  status: PageStatus;
  new_name?: string;
}

export interface SaveTemplateResponse {
  config_url: string;
  html_url: string;
  id: string;
  name: string;
  saved_files: string[];
  status: string;
}

export interface PublishTemplateResponse {
  added_to_ps: boolean;
}

export const pagesApi = createApi({
  reducerPath: "pagesApi",
  baseQuery: axiosBaseQuery({ baseUrl: process.env.API_URL as string }),
  tagTypes: ["Templates"],
  endpoints: (builder) => ({
    getPages: builder.query<Templates, Record<string, any>>({
      query: (params) => ({
        url: `/read/`,
        params,
      }),
      providesTags: ["Templates"],
    }),
    saveTemplate: builder.mutation<
      SaveTemplateResponse,
      { type: PageTypes; data: { name: string; formData: FormData } }
    >({
      query: ({ type, data: { name, formData } }) => ({
        url: `/save/${type}`,
        method: "POST",
        params: { name },
        data: formData,
      }),
      invalidatesTags: ["Templates"],
    }),
    deleteTemplate: builder.mutation<void, DeleteTemplatePayload>({
      query: ({ id, status, type }) => ({
        url: `/template/${type}/${status}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Templates"],
    }),
    updateTemplateName: builder.mutation<void, { id: string; name: string }>({
      query: ({ id, name }) => ({
        url: `/template/${id}/slug`,
        method: "PUT",
        data: { name },
      }),
      invalidatesTags: ["Templates"],
    }),
    publishTemplate: builder.mutation<
      PublishTemplateResponse,
      { id: string; type: PageTypes }
    >({
      query: ({ id, type }) => ({
        method: "POST",
        url: `/publish/${type}/${id}`,
      }),
      invalidatesTags: ["Templates"],
    }),

    // The live and draft heads of a page, rewritten on the server for its domain's iframe switch
    applyFrameHead: builder.mutation<
      FrameHeadResult,
      { id: string; type: PageTypes | "page" | "job"; replacements: HeadReplacement[] }
    >({
      query: ({ id, type, replacements }) => ({
        method: "POST",
        url: `/frame-head/${type}/${id}`,
        data: { replacements },
      }),
    }),

    cloneTemplate: builder.mutation<SaveTemplateResponse, CloneTemplatePayload>(
      {
        query: ({ id, status, type, new_name }) => ({
          method: "POST",
          url: `/clone/${type}/${status}/${id}`,
          params: new_name ? { new_name } : undefined,
        }),
        invalidatesTags: ["Templates"],
      }
    ),

    getProjectData: builder.query<ProjectData, { url: string }>({
      query: ({ url }) => ({
        url,
      }),
    }),
  }),
});

export const {
  useGetPagesQuery,
  useLazyGetPagesQuery,
  useDeleteTemplateMutation,
  usePublishTemplateMutation,
  useSaveTemplateMutation,
  useUpdateTemplateNameMutation,
  useCloneTemplateMutation,
  useApplyFrameHeadMutation,
  useGetProjectDataQuery,
  useLazyGetProjectDataQuery,
} = pagesApi;
